import React, { useState, useEffect, useCallback } from "react"
import { lumi } from "../lib/lumi"
import {RefreshCw, Check, ChevronDown, ChevronUp, Search, X, Pause, Play} from 'lucide-react'

interface Subscription {
  _id: string
  customerId: string
  productId: string
  customerName?: string
  customerEmail?: string
  productName?: string
  totalMonths: number
  deliveredMonths: number
  deliveryDay: number
  status: "active" | "paused" | "completed"
  startDate: string
  nextDeliveryDate: string
  totalPaid: number
  pricePerMonth: number
  orderId?: string
  pauseReason?: string
  notes?: string
  creator: string
  createdAt: string
  updatedAt: string
}

type TabKey = "all" | "pending" | "completed"

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部訂閱" },
  { key: "pending", label: "本月待出貨" },
  { key: "completed", label: "已完成" },
]

// 計算下一個出貨日期
function calcNextDeliveryDate(deliveryDay: number): string {
  const now = new Date()
  const next = new Date(now.getFullYear(), now.getMonth() + 1, deliveryDay)
  return next.toISOString()
}

// 當月是否應出貨（今天 >= 出貨日，且本月尚未出貨）
function isPendingThisMonth(sub: Subscription): boolean {
  if (sub.status !== "active") return false
  const now = new Date()
  const nextDate = new Date(sub.nextDeliveryDate)
  const sameMonthYear =
    nextDate.getFullYear() === now.getFullYear() &&
    nextDate.getMonth() === now.getMonth()
  return sameMonthYear
}

function formatDate(iso: string) {
  if (!iso) return "—"
  const d = new Date(iso)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`
}

const statusLabel: Record<string, { label: string; color: string }> = {
  active: { label: "進行中", color: "bg-emerald-900/30 text-emerald-400 border border-emerald-800" },
  paused: { label: "暫停", color: "bg-amber-900/30 text-amber-400 border border-amber-800" },
  completed: { label: "已完成", color: "bg-gray-800 text-gray-400 border border-gray-700" },
}

const SubscriptionManager: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabKey>("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null)

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.subscriptions.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      setSubscriptions(list as Subscription[])
    } catch (err) {
      console.error("[SubscriptionManager] fetch error:", err)
      showToast("載入資料失敗", "error")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSubscriptions()
  }, [fetchSubscriptions])

  // 暫停訂閱
  const [pauseModalId, setPauseModalId] = useState<string | null>(null)
  const [pauseReasonInput, setPauseReasonInput] = useState("")

  const handlePause = async (sub: Subscription) => {
    if (!pauseReasonInput.trim()) {
      showToast("請填寫暫停原因", "error")
      return
    }
    setProcessingId(sub._id)
    try {
      await lumi.entities.subscriptions.update(sub._id, {
        status: "paused",
        pauseReason: pauseReasonInput.trim(),
        updatedAt: new Date().toISOString(),
      })
      showToast(`已暫停訂閱：${pauseReasonInput}`)
      setPauseModalId(null)
      setPauseReasonInput("")
      await fetchSubscriptions()
    } catch (err) {
      console.error("[SubscriptionManager] pause error:", err)
      showToast("暫停失敗，請稍後再試", "error")
    } finally {
      setProcessingId(null)
    }
  }

  // 恢復訂閱
  const handleResume = async (sub: Subscription) => {
    if (processingId) return
    setProcessingId(sub._id)
    try {
      const nextDeliveryDate = calcNextDeliveryDate(sub.deliveryDay)
      await lumi.entities.subscriptions.update(sub._id, {
        status: "active",
        pauseReason: "",
        nextDeliveryDate,
        updatedAt: new Date().toISOString(),
      })
      showToast("訂閱已恢復，下次出貨日已重新計算")
      await fetchSubscriptions()
    } catch (err) {
      console.error("[SubscriptionManager] resume error:", err)
      showToast("恢復失敗，請稍後再試", "error")
    } finally {
      setProcessingId(null)
    }
  }

  // 手動勾選「已出貨」
  const handleMarkDelivered = async (sub: Subscription) => {
    if (processingId) return
    setProcessingId(sub._id)
    try {
      const newDeliveredMonths = sub.deliveredMonths + 1
      const isCompleted = newDeliveredMonths >= sub.totalMonths
      const nextDeliveryDate = isCompleted
        ? sub.nextDeliveryDate
        : calcNextDeliveryDate(sub.deliveryDay)
      const newStatus: Subscription["status"] = isCompleted ? "completed" : "active"

      await lumi.entities.subscriptions.update(sub._id, {
        deliveredMonths: newDeliveredMonths,
        nextDeliveryDate,
        status: newStatus,
        updatedAt: new Date().toISOString(),
      })

      showToast(
        isCompleted
          ? `訂閱已完成！共出貨 ${newDeliveredMonths} 個月`
          : `已登記出貨，進度更新為 ${newDeliveredMonths}/${sub.totalMonths}`
      )
      await fetchSubscriptions()
    } catch (err) {
      console.error("[SubscriptionManager] update error:", err)
      showToast("更新失敗，請稍後再試", "error")
    } finally {
      setProcessingId(null)
    }
  }

  // 篩選邏輯
  const filteredData = subscriptions.filter((sub) => {
    const q = searchQuery.toLowerCase()
    const matchSearch =
      !q ||
      sub.customerName?.toLowerCase().includes(q) ||
      sub.customerEmail?.toLowerCase().includes(q) ||
      sub.productName?.toLowerCase().includes(q) ||
      sub.customerId?.toLowerCase().includes(q)

    if (activeTab === "pending") {
      return matchSearch && isPendingThisMonth(sub)
    }
    if (activeTab === "completed") {
      return matchSearch && sub.status === "completed"
    }
    // all tab
    const matchStatus = statusFilter === "all" || sub.status === statusFilter
    return matchSearch && matchStatus
  })

  const pendingCount = subscriptions.filter(isPendingThisMonth).length

  return (
    <div className="p-8 min-h-full bg-gray-900">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 px-5 py-3 text-sm font-light rounded shadow-lg transition-all ${
            toast.type === "success"
              ? "bg-emerald-800 text-emerald-100 border border-emerald-700"
              : "bg-red-900 text-red-200 border border-red-700"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-600 flex items-center justify-center">
              <RefreshCw size={20} className="text-white" />
            </div>
            <h2 className="text-2xl font-light tracking-wider text-gray-100">
              訂閱出貨管理
            </h2>
          </div>
          <p className="text-gray-400 text-sm font-light pl-13">
            管理所有訂閱訂單，手動登記每月出貨進度
          </p>
        </div>
        <button
          onClick={fetchSubscriptions}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 text-sm font-light transition-colors"
        >
          <RefreshCw size={14} />
          重新整理
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-950 border border-gray-800 p-4">
          <div className="text-2xl font-light text-gray-100">{subscriptions.length}</div>
          <div className="text-xs text-gray-500 mt-1">全部訂閱</div>
        </div>
        <div className="bg-gray-950 border border-amber-800/40 p-4">
          <div className="text-2xl font-light text-amber-400">{pendingCount}</div>
          <div className="text-xs text-gray-500 mt-1">本月待出貨</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-4">
          <div className="text-2xl font-light text-emerald-400">
            {subscriptions.filter((s) => s.status === "completed").length}
          </div>
          <div className="text-xs text-gray-500 mt-1">已完成</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-3 text-sm font-light tracking-wide transition-colors relative ${
              activeTab === tab.key
                ? "text-white border-b-2 border-white -mb-px"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {tab.label}
            {tab.key === "pending" && pendingCount > 0 && (
              <span className="ml-2 text-xs bg-amber-600 text-white px-1.5 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search & Filter (全部 tab 才顯示狀態篩選) */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="搜尋客戶姓名、Email、商品名稱..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-gray-950 border border-gray-800 text-gray-200 text-sm font-light focus:outline-none focus:border-gray-600 placeholder-gray-600"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {activeTab === "all" && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-gray-950 border border-gray-800 text-gray-300 text-sm font-light focus:outline-none"
          >
            <option value="all">所有狀態</option>
            <option value="active">進行中</option>
            <option value="paused">暫停</option>
            <option value="completed">已完成</option>
          </select>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500 text-sm">
          載入中...
        </div>
      ) : filteredData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-600">
          <RefreshCw size={32} className="mb-3 opacity-30" />
          <p className="text-sm font-light">
            {activeTab === "pending" ? "本月沒有待出貨的訂閱" : "尚無訂閱資料"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredData.map((sub) => {
            const progress = `${sub.deliveredMonths}/${sub.totalMonths}`
            const progressPct = Math.min((sub.deliveredMonths / sub.totalMonths) * 100, 100)
            const st = statusLabel[sub.status] || statusLabel.active
            const isExpanded = expandedId === sub._id
            const isProcessing = processingId === sub._id
            const canDeliver = sub.status === "active"

            return (
              <div
                key={sub._id}
                className="bg-gray-950 border border-gray-800 hover:border-gray-700 transition-colors"
              >
                {/* Row */}
                <div className="px-5 py-4 flex items-center gap-4">
                  {/* 出貨按鈕 */}
                  <button
                    onClick={() => canDeliver && handleMarkDelivered(sub)}
                    disabled={!canDeliver || isProcessing}
                    title={canDeliver ? "標記為已出貨" : "此訂閱不可操作"}
                    className={`flex-shrink-0 w-8 h-8 border flex items-center justify-center transition-all ${
                      canDeliver
                        ? "border-gray-600 hover:border-emerald-500 hover:bg-emerald-900/20 text-gray-500 hover:text-emerald-400 cursor-pointer"
                        : "border-gray-800 text-gray-700 cursor-not-allowed"
                    } ${isProcessing ? "opacity-50" : ""}`}
                  >
                    {isProcessing ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} />
                    )}
                  </button>

                  {/* 主要資訊 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-light text-gray-100">
                        {sub.customerName || sub.customerId}
                      </span>
                      {sub.customerEmail && (
                        <span className="text-xs text-gray-500">{sub.customerEmail}</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${st.color}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 font-light">
                      {sub.productName || sub.productId}
                      <span className="mx-2 text-gray-700">·</span>
                      每月 {sub.deliveryDay} 日出貨
                      <span className="mx-2 text-gray-700">·</span>
                      NT${sub.pricePerMonth?.toLocaleString()} / 月
                    </div>
                  </div>

                  {/* 進度 */}
                  <div className="flex-shrink-0 w-32 hidden sm:block">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400 font-light">進度</span>
                      <span className="text-xs text-gray-300 font-light">{progress}</span>
                    </div>
                    <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>

                  {/* 下次出貨 */}
                  <div className="flex-shrink-0 hidden md:block text-right">
                    <div className="text-xs text-gray-500 font-light">下次出貨</div>
                    <div className="text-sm text-gray-300 font-light">
                      {sub.status === "completed" ? "—" : formatDate(sub.nextDeliveryDate)}
                    </div>
                  </div>

                  {/* 暫停 / 恢復 按鈕 */}
                  {sub.status === "active" && (
                    <button
                      onClick={() => { setPauseModalId(sub._id); setPauseReasonInput("") }}
                      title="暫停訂閱"
                      className="flex-shrink-0 w-8 h-8 border border-gray-700 flex items-center justify-center text-gray-500 hover:border-amber-500 hover:text-amber-400 hover:bg-amber-900/20 transition-all"
                    >
                      <Pause size={13} />
                    </button>
                  )}
                  {sub.status === "paused" && (
                    <button
                      onClick={() => handleResume(sub)}
                      disabled={processingId === sub._id}
                      title="恢復訂閱"
                      className="flex-shrink-0 w-8 h-8 border border-gray-700 flex items-center justify-center text-gray-500 hover:border-emerald-500 hover:text-emerald-400 hover:bg-emerald-900/20 transition-all disabled:opacity-50"
                    >
                      <Play size={13} />
                    </button>
                  )}

                  {/* 展開按鈕 */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : sub._id)}
                    className="flex-shrink-0 text-gray-600 hover:text-gray-300 transition-colors"
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                {/* 展開詳情 */}
                {isExpanded && (
                  <div className="px-5 pb-4 pt-2 border-t border-gray-800">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-light">
                      <div>
                        <div className="text-gray-600 mb-0.5">訂閱開始日</div>
                        <div className="text-gray-300">{formatDate(sub.startDate)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600 mb-0.5">總預收金額</div>
                        <div className="text-gray-300">NT${sub.totalPaid?.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-gray-600 mb-0.5">關聯訂單</div>
                        <div className="text-gray-300 truncate">{sub.orderId || "—"}</div>
                      </div>
                      <div>
                        <div className="text-gray-600 mb-0.5">建立時間</div>
                        <div className="text-gray-300">{formatDate(sub.createdAt)}</div>
                      </div>
                      {sub.pauseReason && (
                        <div className="col-span-2 sm:col-span-4">
                          <div className="text-gray-600 mb-0.5">暫停原因</div>
                          <div className="text-amber-400">{sub.pauseReason}</div>
                        </div>
                      )}
                      {sub.notes && (
                        <div className="col-span-2 sm:col-span-4">
                          <div className="text-gray-600 mb-0.5">備註</div>
                          <div className="text-gray-400">{sub.notes}</div>
                        </div>
                      )}
                    </div>
                    {/* 行動版進度 */}
                    <div className="mt-4 sm:hidden">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500 font-light">出貨進度</span>
                        <span className="text-xs text-gray-300 font-light">{progress}</span>
                      </div>
                      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-600 rounded-full"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* 使用說明 */}
      <div className="mt-8 p-4 bg-gray-950 border border-gray-800 text-xs text-gray-600 font-light leading-relaxed">
        <span className="text-gray-500">操作說明：</span>
        點擊左側 ✓ 登記已出貨（deliveredMonths+1）；點擊 ⏸ 暫停並填寫原因；點擊 ▶ 恢復訂閱並自動重算下次出貨日。
      </div>

      {/* 暫停 Modal */}
      {pauseModalId && (() => {
        const sub = subscriptions.find(s => s._id === pauseModalId)
        if (!sub) return null
        return (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-gray-950 border border-gray-800 w-full max-w-md p-6">
              <h3 className="text-base font-light text-gray-100 mb-1">暫停訂閱</h3>
              <p className="text-xs text-gray-500 mb-5">
                {sub.customerName || sub.customerId} — {sub.productName || sub.productId}
              </p>
              <label className="block text-xs text-gray-500 mb-2 uppercase tracking-wider">暫停原因（必填）</label>
              <textarea
                value={pauseReasonInput}
                onChange={e => setPauseReasonInput(e.target.value)}
                placeholder="例：客戶申請暫停、地址更新中..."
                rows={3}
                className="w-full bg-gray-900 border border-gray-700 text-gray-200 text-sm font-light px-3 py-2 focus:outline-none focus:border-amber-600 placeholder-gray-600 resize-none"
              />
              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => handlePause(sub)}
                  disabled={processingId === sub._id || !pauseReasonInput.trim()}
                  className="flex-1 py-2.5 bg-amber-700 hover:bg-amber-600 text-white text-sm font-light transition-colors disabled:opacity-50"
                >
                  {processingId === sub._id ? "處理中..." : "確認暫停"}
                </button>
                <button
                  onClick={() => { setPauseModalId(null); setPauseReasonInput("") }}
                  className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-light transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export default SubscriptionManager
